import React, { useState, useEffect, useCallback } from 'react';
import {
  Layers, Search, ChevronDown, ChevronRight, FileText, Shield, 
  AlertTriangle, CheckCircle2, HelpCircle, XCircle, Clock,
  Database, FolderOpen, Eye, RefreshCw, ArrowUpDown, Loader2,
  Link2, Filter, BarChart3, Lock, Users, Globe, Server, 
  BookOpen, Repeat2, ClipboardCheck, Info, Zap, ListChecks,
  CircleDot
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import api from '@/services/api';

const STATUS_CONFIG = {
  implemented: { label: 'Implemented', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
  planned: { label: 'Planned', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Clock },
  not_applicable: { label: 'N/A', color: 'bg-muted/50 text-muted-foreground border-border/30', icon: XCircle },
  unknown: { label: 'Unknown', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30', icon: HelpCircle },
};

const COVERAGE_CONFIG = {
  full: { label: 'Full', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  partial: { label: 'Partial', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  out_of_scope: { label: 'Out of Scope', color: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const DOC_STATUS_CONFIG = {
  available: { label: 'Available', color: 'text-emerald-400' },
  draft_document_needed: { label: 'Draft Needed', color: 'text-amber-400' },
  missing_evidence: { label: 'Missing', color: 'text-red-400' },
  not_required: { label: 'Not Required', color: 'text-muted-foreground/50' },
  discovery_needed: { label: 'Discovery Needed', color: 'text-blue-400' },
};

const DOMAIN_ICONS = {
  'Governance & Organization': Shield,
  'Risk Management': AlertTriangle,
  'Access Control': Lock,
  'Data Protection & Privacy': Eye,
  'Network & Infrastructure Security': Server,
  'Incident Management': AlertTriangle,
  'Business Continuity & Disaster Recovery': RefreshCw,
  'Human Resource Security': Users,
  'Physical Security': Globe,
  'Change Management & SDLC': ArrowUpDown,
  'Third-Party & Vendor Management': Link2,
  'Audit, Logging & Monitoring': Database,
  'Cryptography & Key Management': Lock,
  'Asset Management': FolderOpen,
  'Awareness & Training': BookOpen,
  'Configuration & Vulnerability Management': BarChart3,
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`${cfg.color} gap-1 text-xs font-medium`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}

function CoverageBadge({ coverage }) {
  const cfg = COVERAGE_CONFIG[coverage] || COVERAGE_CONFIG.full;
  return (
    <Badge variant="outline" className={`${cfg.color} text-xs`}>
      {cfg.label}
    </Badge>
  );
}

function EnabledFrameworksBanner({ enabledFrameworks, totalApplicable, onResync, resyncing }) {
  if (!enabledFrameworks || enabledFrameworks.length === 0) {
    return (
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4" data-testid="no-frameworks-banner">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-amber-300">No Frameworks Enabled</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Enable compliance frameworks from the Frameworks page to populate controls, policies, documents, and evidence.
        </p>
      </div>
    );
  }

  return (
    <Card className="bg-card border" data-testid="enabled-frameworks-banner">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-foreground">
              Active Frameworks ({enabledFrameworks.length})
            </span>
            <span className="text-xs text-muted-foreground/50">
              {totalApplicable} applicable controls
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onResync}
            disabled={resyncing}
            className="border hover:bg-muted text-xs"
            data-testid="resync-btn"
          >
            {resyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Re-sync
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {enabledFrameworks.map((fw, i) => (
            <Badge key={i} variant="outline" className="text-xs bg-cyan-500/10 text-cyan-300 border-cyan-500/30">
              {fw.code || fw}
              {fw.total_controls ? <span className="ml-1 text-muted-foreground/50">({fw.total_controls})</span> : null}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FrameworkMappingRow({ mapping }) {
  return (
    <TableRow className="border hover:bg-muted/40">
      <TableCell className="font-mono text-xs text-cyan-400">{mapping.framework}</TableCell>
      <TableCell className="font-mono text-xs text-foreground">{mapping.control_id}</TableCell>
      <TableCell className="text-xs text-muted-foreground max-w-md">{mapping.intent}</TableCell>
      <TableCell><CoverageBadge coverage={mapping.coverage} /></TableCell>
    </TableRow>
  );
}

function DocumentRequirement({ doc, index }) {
  const statusCfg = DOC_STATUS_CONFIG[doc.status] || DOC_STATUS_CONFIG.discovery_needed;
  return (
    <div className="border border rounded-lg p-4 bg-card/50" data-testid={`doc-req-${index}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-medium text-foreground">{doc.title_recommendation}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs bg-violet-500/10 text-violet-300 border-violet-500/30">
            {doc.document_type}
          </Badge>
          <span className={`text-xs ${statusCfg.color}`}>{statusCfg.label}</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground/50 mb-3">{doc.description}</p>
      <div className="flex flex-wrap gap-1 mb-2">
        {doc.minimal_fields?.map((f, i) => (
          <Badge key={i} variant="outline" className="text-[10px] bg-muted text-muted-foreground border">{f}</Badge>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground">Update frequency: <span className="text-muted-foreground">{doc.update_frequency}</span></div>
    </div>
  );
}

function EvidenceRequirement({ ev, index }) {
  const statusCfg = DOC_STATUS_CONFIG[ev.status] || DOC_STATUS_CONFIG.discovery_needed;
  return (
    <div className="border border rounded-lg p-4 bg-card/50" data-testid={`ev-req-${index}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-cyan-400" />
          <Badge variant="outline" className="text-[10px] bg-cyan-500/10 text-cyan-300 border-cyan-500/30">
            {ev.evidence_type}
          </Badge>
          <span className={`text-xs ${statusCfg.color}`}>{statusCfg.label}</span>
        </div>
        {ev.automatable && (
          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
            Automatable
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-2">{ev.description}</p>
      <div className="grid grid-cols-3 gap-2 text-[10px]">
        <div><span className="text-muted-foreground">Source:</span> <span className="text-muted-foreground">{ev.source_system}</span></div>
        <div><span className="text-muted-foreground">Method:</span> <span className="text-muted-foreground">{ev.collection_method}</span></div>
        <div><span className="text-muted-foreground">Frequency:</span> <span className="text-muted-foreground">{ev.collection_frequency}</span></div>
      </div>
    </div>
  );
}

function DiscoveryChecklist({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="p-4 bg-card rounded-lg border border" data-testid="discovery-checklist">
      <h4 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
        <ListChecks className="w-4 h-4" />
        Discovery Checklist
      </h4>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
            <CircleDot className="w-3 h-3 mt-0.5 text-blue-400 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ControlDetailDialog({ control, open, onClose, onStatusUpdate }) {
  const [newStatus, setNewStatus] = useState(control?.implementation_status || 'unknown');
  const [notes, setNotes] = useState(control?.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (control) {
      setNewStatus(control.implementation_status || 'unknown');
      setNotes(control.notes || '');
    }
  }, [control]);

  if (!control) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/grc/cross-framework-map/${control.id}/status`, {
        implementation_status: newStatus,
        notes,
      });
      toast.success('Status updated');
      onStatusUpdate();
      onClose();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const sl = control.status_layer || {};
  const activeMappings = control.active_framework_mappings || control.framework_mappings || [];
  const allMappings = control.framework_mappings || [];
  const definition = control.definition || '';
  const discoveryChecklist = control.discovery_checklist || sl.discovery_checklist || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span className="font-mono text-cyan-400">{control.id}</span>
            <span className="text-foreground">{control.name}</span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground/50">{control.description}</DialogDescription>
        </DialogHeader>

        {/* Definition */}
        {definition && (
          <div className="p-3 bg-card/80 rounded-lg border border" data-testid="control-definition">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-violet-400" />
              <span className="text-xs font-medium text-violet-300">Control Definition</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{definition}</p>
          </div>
        )}

        {/* Status Update */}
        <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border" data-testid="status-update-section">
          <span className="text-xs text-muted-foreground/50 shrink-0">Status:</span>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger className="w-40 h-8 bg-muted border text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border">
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="flex-1 h-8 bg-muted border text-xs"
            data-testid="control-notes-input"
          />
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-xs" data-testid="save-status-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
          </Button>
        </div>

        <Tabs defaultValue="mappings" className="mt-2">
          <TabsList className="bg-card border border flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="mappings" className="text-xs data-[state=active]:bg-muted">
              Active Mappings ({activeMappings.length})
            </TabsTrigger>
            <TabsTrigger value="all-mappings" className="text-xs data-[state=active]:bg-muted">
              All Mappings ({allMappings.length})
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-xs data-[state=active]:bg-muted">
              Documents ({sl.documents?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="evidence" className="text-xs data-[state=active]:bg-muted">
              Evidence ({sl.evidence?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="discovery" className="text-xs data-[state=active]:bg-muted">
              Discovery
            </TabsTrigger>
            {sl.na_justification && (
              <TabsTrigger value="justification" className="text-xs data-[state=active]:bg-muted">N/A Justification</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="mappings" className="mt-3">
            <div className="text-xs text-muted-foreground/50 mb-2">Showing mappings for your enabled frameworks only</div>
            <Table>
              <TableHeader>
                <TableRow className="border">
                  <TableHead className="text-xs text-muted-foreground/50">Framework</TableHead>
                  <TableHead className="text-xs text-muted-foreground/50">Control ID</TableHead>
                  <TableHead className="text-xs text-muted-foreground/50">Intent</TableHead>
                  <TableHead className="text-xs text-muted-foreground/50">Coverage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeMappings.map((m, i) => <FrameworkMappingRow key={i} mapping={m} />)}
                {activeMappings.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground/50 py-6">No active framework mappings</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="all-mappings" className="mt-3">
            <div className="text-xs text-muted-foreground/50 mb-2">All framework mappings (including non-enabled)</div>
            <Table>
              <TableHeader>
                <TableRow className="border">
                  <TableHead className="text-xs text-muted-foreground/50">Framework</TableHead>
                  <TableHead className="text-xs text-muted-foreground/50">Control ID</TableHead>
                  <TableHead className="text-xs text-muted-foreground/50">Intent</TableHead>
                  <TableHead className="text-xs text-muted-foreground/50">Coverage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allMappings.map((m, i) => <FrameworkMappingRow key={i} mapping={m} />)}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="documents" className="mt-3 space-y-3">
            {sl.documents?.map((doc, i) => <DocumentRequirement key={i} doc={doc} index={i} />)}
            {(!sl.documents || sl.documents.length === 0) && (
              <p className="text-xs text-muted-foreground/50 text-center py-6">No document requirements</p>
            )}
          </TabsContent>

          <TabsContent value="evidence" className="mt-3 space-y-3">
            {sl.evidence?.map((ev, i) => <EvidenceRequirement key={i} ev={ev} index={i} />)}
            {(!sl.evidence || sl.evidence.length === 0) && (
              <p className="text-xs text-muted-foreground/50 text-center py-6">No evidence requirements</p>
            )}
          </TabsContent>

          <TabsContent value="discovery" className="mt-3">
            <DiscoveryChecklist items={discoveryChecklist} />
            {(!discoveryChecklist || discoveryChecklist.length === 0) && (
              <p className="text-xs text-muted-foreground/50 text-center py-6">No discovery checklist items</p>
            )}
          </TabsContent>

          {sl.na_justification && (
            <TabsContent value="justification" className="mt-3">
              <div className="p-4 bg-card rounded-lg border border">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Required Artifacts for Non-Applicability</h4>
                <ul className="space-y-2">
                  {sl.na_justification.required_artifacts?.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <FileText className="w-3 h-3 mt-0.5 text-muted-foreground/50 shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ReusabilityTab({ items }) {
  const [search, setSearch] = useState('');
  const filtered = items.filter(i =>
    i.artifact_name.toLowerCase().includes(search.toLowerCase()) ||
    i.frameworks_covered.some(f => f.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search artifacts or frameworks..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md bg-card border text-sm"
        data-testid="reuse-search"
      />
      <div className="space-y-3">
        {filtered.map((item, i) => (
          <div key={i} className="border border rounded-lg p-4 bg-card/50">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {item.artifact_type === 'document' ? <FileText className="w-4 h-4 text-violet-400" /> : <Database className="w-4 h-4 text-cyan-400" />}
                <span className="text-sm font-medium text-foreground">{item.artifact_name}</span>
              </div>
              <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
                {item.reuse_count} frameworks
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {item.frameworks_covered.map((fw, j) => (
                <Badge key={j} variant="outline" className="text-[10px] bg-muted text-muted-foreground border">{fw}</Badge>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground">
              Controls: {item.controls_covered.join(', ')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CrossFrameworkMapPage() {
  const [controls, setControls] = useState([]);
  const [stats, setStats] = useState(null);
  const [reusability, setReusability] = useState([]);
  const [enabledFrameworkInfo, setEnabledFrameworkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Filters
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('controls');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (domainFilter !== 'all') params.domain = domainFilter;
      if (frameworkFilter !== 'all') params.framework = frameworkFilter;
      if (statusFilter !== 'all') params.implementation_status = statusFilter;
      if (search) params.search = search;

      const [controlsRes, statsRes, enabledRes] = await Promise.all([
        api.get('/grc/cross-framework-map', { params }),
        api.get('/grc/cross-framework-map/stats'),
        api.get('/grc/cross-framework-map/enabled-frameworks'),
      ]);
      setControls(controlsRes.data.items || []);
      setStats(statsRes.data);
      setEnabledFrameworkInfo(enabledRes.data);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [domainFilter, frameworkFilter, statusFilter, search]);

  const fetchReusability = useCallback(async () => {
    try {
      const res = await api.get('/grc/cross-framework-map/reusability');
      setReusability(res.data.items || []);
    } catch (err) {
      console.error('Failed to load reusability', err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTab === 'reusability') fetchReusability(); }, [activeTab, fetchReusability]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/grc/cross-framework-map/seed');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed controls');
    } finally {
      setSeeding(false);
    }
  };

  const handleResync = async () => {
    setResyncing(true);
    try {
      const res = await api.post('/grc/cross-framework-map/resync');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to re-sync controls');
    } finally {
      setResyncing(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openDetail = (ctrl) => {
    setSelectedControl(ctrl);
    setDialogOpen(true);
  };

  const domains = stats?.by_domain ? Object.keys(stats.by_domain) : [];
  const isSeeded = stats?.seeded;
  const enabledFrameworks = enabledFrameworkInfo?.enabled_frameworks || [];
  const enabledCodes = enabledFrameworkInfo?.enabled_codes || stats?.enabled_frameworks || [];

  return (
    <div className="space-y-6 p-1" data-testid="cross-framework-map-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Layers className="w-6 h-6 text-cyan-400" />
            Cross-Framework Control Map
          </h1>
          <p className="text-sm text-muted-foreground/50 mt-1">
            Controls, policies, documents & evidence mapped to your enabled frameworks
          </p>
        </div>
        <div className="flex gap-2">
          {!isSeeded && enabledCodes.length > 0 && (
            <Button onClick={handleSeed} disabled={seeding} className="bg-cyan-600 hover:bg-cyan-500" data-testid="seed-controls-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Initialize Controls
            </Button>
          )}
          <Button variant="outline" onClick={fetchData} className="border hover:bg-muted" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Enabled Frameworks Banner */}
      <EnabledFrameworksBanner
        enabledFrameworks={enabledFrameworks}
        totalApplicable={enabledFrameworkInfo?.total_applicable_controls || 0}
        onResync={handleResync}
        resyncing={resyncing}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-card border">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-foreground">{stats.total_unified_controls}</div>
              <div className="text-xs text-muted-foreground/50">Applicable Controls</div>
            </CardContent>
          </Card>
          <Card className="bg-card border">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-400">{stats.by_status?.implemented || 0}</div>
              <div className="text-xs text-muted-foreground/50">Implemented</div>
            </CardContent>
          </Card>
          <Card className="bg-card border">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-400">{stats.by_status?.planned || 0}</div>
              <div className="text-xs text-muted-foreground/50">Planned</div>
            </CardContent>
          </Card>
          <Card className="bg-card border">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-violet-400">{stats.total_documents_required}</div>
              <div className="text-xs text-muted-foreground/50">Documents Required</div>
            </CardContent>
          </Card>
          <Card className="bg-card border">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-cyan-400">{stats.total_evidence_required}</div>
              <div className="text-xs text-muted-foreground/50">Evidence Required</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Framework Coverage Summary */}
      {stats?.framework_coverage && Object.keys(stats.framework_coverage).length > 0 && (
        <Card className="bg-card border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-foreground">Framework Coverage (Enabled Only)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.framework_coverage).sort((a, b) => b[1].total - a[1].total).map(([fw, cov]) => (
                <TooltipProvider key={fw}>
                  <Tooltip>
                    <TooltipTrigger>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg border border cursor-default">
                        <span className="text-xs font-mono text-cyan-400">{fw}</span>
                        <span className="text-xs text-muted-foreground/50">{cov.total}</span>
                        <div className="flex gap-0.5">
                          {cov.full > 0 && <div className="w-2 h-2 rounded-full bg-emerald-500" title="Full" />}
                          {cov.partial > 0 && <div className="w-2 h-2 rounded-full bg-amber-500" title="Partial" />}
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-muted border text-xs">
                      <p>Full: {cov.full || 0} | Partial: {cov.partial || 0} | OoS: {cov.out_of_scope || 0}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border">
          <TabsTrigger value="controls" className="text-xs data-[state=active]:bg-muted" data-testid="controls-tab">
            Controls
          </TabsTrigger>
          <TabsTrigger value="reusability" className="text-xs data-[state=active]:bg-muted" data-testid="reusability-tab">
            <Repeat2 className="w-3 h-3 mr-1" /> Reusability Map
          </TabsTrigger>
        </TabsList>

        {/* Controls Tab */}
        <TabsContent value="controls">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 mt-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
              <Input
                placeholder="Search controls..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-card border text-sm"
                data-testid="search-controls"
              />
            </div>
            <Select value={domainFilter} onValueChange={setDomainFilter}>
              <SelectTrigger className="w-56 bg-card border text-xs" data-testid="domain-filter">
                <Filter className="w-3 h-3 mr-1" /> <SelectValue placeholder="Domain" />
              </SelectTrigger>
              <SelectContent className="bg-card border">
                <SelectItem value="all" className="text-xs">All Domains</SelectItem>
                {domains.map(d => <SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={frameworkFilter} onValueChange={setFrameworkFilter}>
              <SelectTrigger className="w-44 bg-card border text-xs" data-testid="framework-filter">
                <SelectValue placeholder="Framework" />
              </SelectTrigger>
              <SelectContent className="bg-card border">
                <SelectItem value="all" className="text-xs">All Frameworks</SelectItem>
                {enabledCodes.map(f => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-card border text-xs" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-card border">
                <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Controls Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </div>
          ) : controls.length === 0 ? (
            <div className="text-center py-20">
              <Layers className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground/50">
                {enabledCodes.length === 0
                  ? 'No frameworks enabled. Enable frameworks first to populate controls.'
                  : isSeeded
                    ? 'No controls match your filters.'
                    : 'Controls not initialized. Click "Initialize Controls" to populate based on your enabled frameworks.'}
              </p>
            </div>
          ) : (
            <div className="border border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border bg-card/80">
                    <TableHead className="w-8 text-xs text-muted-foreground/50"></TableHead>
                    <TableHead className="text-xs text-muted-foreground/50 w-32">ID</TableHead>
                    <TableHead className="text-xs text-muted-foreground/50">Control</TableHead>
                    <TableHead className="text-xs text-muted-foreground/50 w-52">Domain</TableHead>
                    <TableHead className="text-xs text-muted-foreground/50 w-24 text-center">Frameworks</TableHead>
                    <TableHead className="text-xs text-muted-foreground/50 w-24 text-center">Docs</TableHead>
                    <TableHead className="text-xs text-muted-foreground/50 w-24 text-center">Evidence</TableHead>
                    <TableHead className="text-xs text-muted-foreground/50 w-28">Status</TableHead>
                    <TableHead className="text-xs text-muted-foreground/50 w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {controls.map((ctrl) => {
                    const expanded = expandedRows.has(ctrl.id);
                    const DomainIcon = DOMAIN_ICONS[ctrl.domain] || Shield;
                    const activeFw = ctrl.active_framework_mappings || [];
                    return (
                      <React.Fragment key={ctrl.id}>
                        <TableRow
                          className="border hover:bg-muted/40 cursor-pointer"
                          onClick={() => toggleRow(ctrl.id)}
                          data-testid={`control-row-${ctrl.id}`}
                        >
                          <TableCell className="p-2">
                            {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground/50" /> : <ChevronRight className="w-4 h-4 text-muted-foreground/50" />}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-cyan-400">{ctrl.id}</TableCell>
                          <TableCell className="text-sm text-foreground">{ctrl.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <DomainIcon className="w-3 h-3 text-muted-foreground/50" />
                              <span className="text-xs text-muted-foreground">{ctrl.domain}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="text-[10px] bg-muted border text-foreground">
                              {activeFw.length || ctrl.framework_mappings?.length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="text-[10px] bg-violet-500/10 border-violet-500/30 text-violet-300">
                              {ctrl.required_documents?.length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="text-[10px] bg-cyan-500/10 border-cyan-500/30 text-cyan-300">
                              {ctrl.required_evidence?.length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell><StatusBadge status={ctrl.implementation_status} /></TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openDetail(ctrl); }}
                              className="h-7 w-7 p-0 hover:bg-muted/30"
                              data-testid={`detail-btn-${ctrl.id}`}
                            >
                              <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* Expanded row */}
                        {expanded && (
                          <TableRow className="border bg-background">
                            <TableCell colSpan={9} className="p-0">
                              <div className="px-6 py-4 space-y-4">
                                {/* Definition */}
                                {ctrl.definition && (
                                  <div className="p-3 bg-card/60 rounded border border">
                                    <div className="flex items-center gap-1.5 mb-1">
                                      <Info className="w-3 h-3 text-violet-400" />
                                      <span className="text-[10px] font-semibold text-violet-300">Definition</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{ctrl.definition}</p>
                                  </div>
                                )}

                                <p className="text-xs text-muted-foreground/50">{ctrl.description}</p>

                                {/* Active Framework Mappings */}
                                <div>
                                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                                    <Link2 className="w-3 h-3" /> Active Framework Mappings
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {(activeFw.length > 0 ? activeFw : ctrl.framework_mappings || []).map((m, i) => (
                                      <div key={i} className="flex items-center gap-2 p-2 bg-card rounded border border">
                                        <span className="font-mono text-[10px] text-cyan-400 w-20 shrink-0">{m.framework}</span>
                                        <span className="text-[10px] text-muted-foreground/50 w-24 shrink-0">{m.control_id}</span>
                                        <CoverageBadge coverage={m.coverage} />
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Documents preview */}
                                <div>
                                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                                    <FileText className="w-3 h-3" /> Required Documents ({ctrl.required_documents?.length || 0})
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {ctrl.status_layer?.documents?.map((d, i) => (
                                      <Badge key={i} variant="outline" className="text-[10px] bg-violet-500/5 text-violet-300 border-violet-500/20">
                                        {d.title_recommendation} <span className={`ml-1 ${DOC_STATUS_CONFIG[d.status]?.color || 'text-muted-foreground/50'}`}>({DOC_STATUS_CONFIG[d.status]?.label})</span>
                                      </Badge>
                                    ))}
                                  </div>
                                </div>

                                {/* Evidence preview */}
                                <div>
                                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                                    <Database className="w-3 h-3" /> Required Evidence ({ctrl.required_evidence?.length || 0})
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {ctrl.status_layer?.evidence?.map((e, i) => (
                                      <Badge key={i} variant="outline" className="text-[10px] bg-cyan-500/5 text-cyan-300 border-cyan-500/20">
                                        {e.evidence_type} {e.automatable && '(auto)'}
                                        <span className={`ml-1 ${DOC_STATUS_CONFIG[e.status]?.color || 'text-muted-foreground/50'}`}>({DOC_STATUS_CONFIG[e.status]?.label})</span>
                                      </Badge>
                                    ))}
                                  </div>
                                </div>

                                {/* Discovery checklist preview */}
                                {ctrl.discovery_checklist && ctrl.discovery_checklist.length > 0 && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                                      <ClipboardCheck className="w-3 h-3" /> Discovery Checklist
                                    </h4>
                                    <ul className="space-y-1">
                                      {ctrl.discovery_checklist.slice(0, 3).map((item, i) => (
                                        <li key={i} className="flex items-start gap-1.5 text-[10px] text-muted-foreground/50">
                                          <CircleDot className="w-2.5 h-2.5 mt-0.5 text-blue-400 shrink-0" />
                                          {item}
                                        </li>
                                      ))}
                                      {ctrl.discovery_checklist.length > 3 && (
                                        <li className="text-[10px] text-muted-foreground ml-4">+{ctrl.discovery_checklist.length - 3} more...</li>
                                      )}
                                    </ul>
                                  </div>
                                )}

                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openDetail(ctrl)}
                                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs"
                                  data-testid={`view-detail-${ctrl.id}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Full Detail
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Reusability Tab */}
        <TabsContent value="reusability" className="mt-4">
          <ReusabilityTab items={reusability} />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <ControlDetailDialog
        control={selectedControl}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onStatusUpdate={fetchData}
      />
    </div>
  );
}
